import React, { useState, useRef, useEffect } from "react";
import {
  Users,
  Plus,
  X,
  Upload,
  Image as ImageIcon,
  Trash2,
  Check,
  User,
} from "lucide-react";
import { toast } from "react-toastify";

const normalizePerformer = (p) =>
  typeof p === "string" ? { name: p, image: null } : { name: p?.name || "", image: p?.image || null };

/**
 * Speakers Section Component (Conference/Trade Mission)
 * Dynamic list where users can add speakers with name and optional image
 */
const PerformersEntertainersSection = ({
  value = [],
  onChange,
  maxSize = 2,
  acceptedFormats = ["image/jpeg", "image/jpg", "image/png", "image/webp"],
  className = "",
}) => {
  const [performers, setPerformers] = useState(Array.isArray(value) ? value.map(normalizePerformer) : []);
  const [newPerformerName, setNewPerformerName] = useState("");
  const [newPerformerImage, setNewPerformerImage] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (value && Array.isArray(value)) {
      setPerformers(value.map(normalizePerformer));
    }
  }, [value]);

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const validateFile = (file) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return false;
    }
    if (!acceptedFormats.includes(file.type)) {
      toast.error(
        `Accepted formats: ${acceptedFormats.map((f) => f.split("/")[1].toUpperCase()).join(", ")}`
      );
      return false;
    }
    if (file.size > maxSize * 1024 * 1024) {
      toast.error(`Image size must be less than ${maxSize}MB`);
      return false;
    }
    return true;
  };

  const handleImageSelect = async (file) => {
    if (!file) return;
    if (!validateFile(file)) return;
    try {
      const base64 = await convertToBase64(file);
      setNewPerformerImage(base64);
      toast.success("Image uploaded");
    } catch {
      toast.error("Failed to upload image");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    handleImageSelect(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleAddPerformer = () => {
    const trimmedName = newPerformerName.trim();
    if (!trimmedName) {
      toast.error("Please enter a speaker name");
      return;
    }
    if (performers.some((p) => p.name === trimmedName)) {
      toast.warning("This speaker is already in the list");
      return;
    }
    const newPerformer = { name: trimmedName, image: newPerformerImage };
    const updated = [...performers, newPerformer];
    setPerformers(updated);
    onChange(updated);
    setNewPerformerName("");
    setNewPerformerImage(null);
    toast.success("Speaker added");
  };

  const handleRemovePerformer = (index) => {
    const updated = performers.filter((_, i) => i !== index);
    setPerformers(updated);
    onChange(updated);
    toast.success("Speaker removed");
  };

  const handleUpdateImage = (index, file) => {
    if (!file) return;
    if (!validateFile(file)) return;
    convertToBase64(file)
      .then((base64) => {
        const updated = [...performers];
        updated[index].image = base64;
        setPerformers(updated);
        onChange(updated);
        toast.success("Image updated");
      })
      .catch(() => toast.error("Failed to update image"));
  };

  return (
    <div className={className}>
      <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl">
        <div className="flex items-center mb-4 space-x-2">
          <Users className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-bold text-gray-900">Speakers</h3>
          <span className="px-2 py-0.5 text-xs font-semibold text-purple-700 bg-purple-100 rounded-full">
            {performers.length}
          </span>
        </div>

        <p className="mb-4 text-xs text-gray-600">
          Add speakers for your event. You can optionally upload a speaker image for each. You can add multiple.
        </p>

        <div className="p-4 mb-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="space-y-3">
            <div>
              <label className="block mb-1.5 text-xs font-semibold text-gray-700">Speaker Name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                <input
                  type="text"
                  value={newPerformerName}
                  onChange={(e) => setNewPerformerName(e.target.value)}
                  placeholder="Enter speaker name..."
                  className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all bg-white"
                />
              </div>
            </div>

            <div>
              <label className="block mb-1.5 text-xs font-semibold text-gray-700">Speaker Image (optional)</label>
              {newPerformerImage ? (
                <div className="relative inline-block">
                  <div className="overflow-hidden w-24 h-24 bg-gray-100 rounded-lg border-2 border-gray-200">
                    <img src={newPerformerImage} alt="Preview" className="object-contain w-full h-full p-2" />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setNewPerformerImage(null);
                      fileInputRef.current?.click();
                    }}
                    className="absolute -top-2 -right-2 p-1 text-white bg-blue-600 rounded-full shadow-lg hover:bg-blue-700"
                    title="Change image"
                  >
                    <ImageIcon className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewPerformerImage(null)}
                    className="absolute -bottom-2 -right-2 p-1 text-white bg-red-600 rounded-full shadow-lg hover:bg-red-700"
                    title="Remove image"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center justify-center space-x-2 px-4 py-2.5 w-full text-sm font-medium text-gray-700 bg-white border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-400 hover:bg-purple-50"
                >
                  <Upload className="w-4 h-4" />
                  <span>Upload Image</span>
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={handleAddPerformer}
              disabled={!newPerformerName.trim()}
              className="flex items-center justify-center space-x-2 px-4 py-2.5 w-full text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 rounded-lg shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              <span>Add Speaker</span>
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedFormats.join(",")}
            onChange={handleFileChange}
            className="hidden"
          />
        </div>

        {performers.length > 0 ? (
          <div className="space-y-3">
            <p className="text-xs font-medium text-gray-700">Added Speakers ({performers.length}):</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {performers.map((performer, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-3 p-3 bg-purple-50 border border-purple-200 rounded-lg group hover:bg-purple-100 transition-colors"
                >
                  <div className="flex-shrink-0">
                    {performer.image ? (
                      <div className="relative">
                        <div className="overflow-hidden w-16 h-16 bg-white rounded-lg border-2 border-purple-300">
                          <img src={performer.image} alt={performer.name} className="object-contain w-full h-full p-2" />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const input = document.createElement("input");
                            input.type = "file";
                            input.accept = acceptedFormats.join(",");
                            input.onchange = (e) => {
                              const file = e.target.files?.[0];
                              if (file) handleUpdateImage(index, file);
                            };
                            input.click();
                          }}
                          className="absolute -top-1 -right-1 p-1 text-white bg-blue-600 rounded-full shadow opacity-0 group-hover:opacity-100"
                          title="Change image"
                        >
                          <ImageIcon className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center w-16 h-16 bg-white rounded-lg border-2 border-gray-300">
                        <User className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{performer.name}</p>
                    {performer.image && (
                      <p className="text-xs text-green-600 flex items-center mt-1">
                        <Check className="w-3 h-3 mr-1" />
                        Image uploaded
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemovePerformer(index)}
                    className="flex-shrink-0 p-2 text-red-600 bg-white rounded-lg border border-red-200 hover:bg-red-50 hover:border-red-300"
                    title="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="py-8 text-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
            <Users className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p className="text-sm font-medium text-gray-600">No speakers added yet</p>
            <p className="mt-1 text-xs text-gray-500">Use the form above to add speakers</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PerformersEntertainersSection;
